import { TRPCClientError } from "@trpc/client"
import type { MutationMeta, QueryMeta } from "@tanstack/react-query"
import type { Store } from "redux"
import ResponseError from "#/types/responseError"
import { HttpStatusCodes } from "#/utils/HttpStatusCodes"
import actionTypes from "#/redux/actions/actionTypes"
import { reportError } from "#/utils/ErrorReporting"
import { reportTaggedQueryError } from "#/utils/reportTaggedQueryError"

// The store is created in main.tsx; register it after configureStore() so
// this module doesn't have to import main.tsx (which would loop imports).
let boundStore: Store | undefined

export function registerQueryErrorStore(store: Store): void {
  boundStore = store
}

// tRPC errors surface upstream HTTP status inside TRPCClientError.data.httpStatus
// (react-query only ever sees the JS error, not a Response). Bridge those into
// the same shared handling as legacy carrot-api ResponseErrors so migrated BFF
// calls trigger session-expiration / 500-reporting like they did before.
function statusFromError(error: unknown): number | undefined {
  if (error instanceof ResponseError) return error?.response?.status
  if (error instanceof TRPCClientError) {
    const data = (error as TRPCClientError<never>).data as { httpStatus?: number } | undefined
    return data?.httpStatus
  }
  return undefined
}

// Fires SESSION_EXPIRED if the error is a 401. Returns true if it dispatched,
// letting callers (e.g. imperative tRPC catches) decide whether to also swallow
// or re-throw. Safe to call before the store is registered — it just no-ops.
export function dispatchSessionExpiredIfUnauthorized(error: unknown): boolean {
  const statusCode = statusFromError(error)
  if (statusCode !== HttpStatusCodes.UNAUTHORIZED) return false
  boundStore?.dispatch({ type: actionTypes.SESSION_EXPIRED })
  return true
}

// QueryCache / MutationCache onError handler. Handles both auth expiration
// and the pre-existing 500-to-Sentry reporting — now covering both the
// legacy `ResponseError` and migrated `TRPCClientError` paths so a 500
// from the BFF reaches Sentry the same way carrot-api 500s always have.
export function handleQueryError(error: unknown, meta?: QueryMeta | MutationMeta): void {
  if (dispatchSessionExpiredIfUnauthorized(error)) return
  // After the 401 check so a session expiry isn't reported as a domain error.
  reportTaggedQueryError(error, meta)
  const statusCode = statusFromError(error)
  if (statusCode !== HttpStatusCodes.INTERNAL_SERVER_ERROR) return

  if (error instanceof ResponseError) {
    const {
      status: responseStatus,
      headers: responseHeaders,
      url: responseUrl,
      redirected: responseWasRedirected
    } = error?.response || {}
    reportError(error, {
      // @ts-expect-error TS(2322) FIXME: Type 'number' is not assignable to type 'string'.
      responseStatus,
      // @ts-expect-error TS(2322) FIXME: Type 'Headers' is not assignable to type 'string'.
      responseHeaders,
      responseUrl,
      // @ts-expect-error TS(2322) FIXME: Type 'boolean' is not assignable to type 'string'.
      responseWasRedirected
    })
    return
  }

  if (error instanceof TRPCClientError) {
    // TRPCClientError doesn't carry the Response object, so we surface
    // what we have — the httpStatus and the tRPC error code — as strings
    // to match reportError's context signature.
    const data = (error as TRPCClientError<never>).data as { httpStatus?: number; code?: string } | undefined
    reportError(error, {
      responseStatus: String(statusCode),
      trpcCode: data?.code ?? "UNKNOWN"
    })
  }
}
